import React, { useEffect, useState } from 'react';
import {
    Table, Input, Button, Select, Space, Tag, message, Image,
    Modal, Descriptions, Divider, Timeline, Typography
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';
import { dateFormat } from '../../Utils/formatDate';

const { Option } = Select;

const ProfileRequest = () => {
    const token = useSelector((state) => state.user.token);
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sorter, setSorter] = useState({ field: 'created_at', order: 'descend' });
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const fetchData = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const res = await apiCall({
                method: 'GET',
                url: `profilerequest?page=${page}&limit=${limit}&search=${search}&status=${statusFilter}&sort=${sorter.field}&order=${sorter.order === 'ascend' ? 'asc' : 'desc'}`,
                data: {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            });

            if (res.status === 'success') {
                const updatedData = res.data.map((item) => ({
                    ...item,
                    parsedJson: item.json ? JSON.parse(item.json) : {},
                }));
                setData(updatedData);
                setMeta(res.meta);
            } else {
                message.error(res.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error(error);
            message.error('An error occurred while fetching data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(meta.page, meta.limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, statusFilter, sorter]);

    const handleTableChange = (pagination, filters, sorterObj) => {
        setMeta((prev) => ({
            ...prev,
            page: pagination.current,
            limit: pagination.pageSize,
        }));
        setSorter({
            field: sorterObj.field || 'created_at',
            order: sorterObj.order || 'descend',
        });
    };

    const handleBulkAction = async (status, reason = '') => {
        try {
            const res = await apiCall({
                method: 'PUT',
                url: '/profilerequestbulk',
                data: {
                    data: {
                        ids: selectedRowKeys,
                        status,
                        reason,
                    },
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                },
            });
            if (res.status === 'success') {
                message.success(res.message);
                setSelectedRowKeys([]);
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message || 'Gagal memproses data');
            }
        } catch (error) {
            console.error(error);
            message.error('Terjadi kesalahan saat memproses data');
        }
    };

    const renderValue = (val) => {
        if (!val || val === '') return '-';

        // Cek jika val adalah string image URL
        if (typeof val === 'string' && val.match(/\.(jpg|jpeg|png|gif|webp|png)$/i)) {
            return <Image src={val} width={80} />;
        }

        // Boolean case
        if (typeof val === 'boolean') return val ? 'Yes' : 'No';

        // Tanggal
        if (typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))) {
            return dateFormat(val); // Pastikan kamu punya fungsi dateFormat
        }

        return val;
    };


    const columns = [
        {
            title: 'No',
            render: (_, __, index) => (meta.page - 1) * meta.limit + index + 1,
        },
        {
            title: 'Users Code',
            dataIndex: 'users_code',
            sorter: true,
            render: (_, record) => record.users?.users_code,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            render: (_, record) => record.users?.name,
        },
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            render: (_, record) =>
                record.image ? <Image src={record.image} height={60} /> : '-',
        },
        {
            title: 'Deskripsi',
            dataIndex: 'desc',
            key: 'desc',
            sorter: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => {
                const color = {
                    pending: 'orange',
                    approved: 'green',
                    rejected: 'red',
                }[status];
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            },
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            sorter: true,
            render: (text, record) => {
                return record.created_at !== "" ? dateFormat(record.created_at) : "-";
            }
        },
        {
            title: 'Actions',
            render: (_, record) => {
                return (
                    <Space>
                        <Button
                            size="small"
                            onClick={() => {
                                setSelectedRecord(record);
                                setIsModalVisible(true);
                            }}
                        >
                            Detail
                        </Button>
                    </Space>
                );
            },
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
        getCheckboxProps: (record) => ({
            disabled: record.status !== 'pending',
        }),
    };
    
    const parsedRecord = selectedRecord?.json ? JSON.parse(selectedRecord.json) : {};

    return (
        <div>
            <h2>Profile Update Requests</h2>
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                <Input
                    placeholder="Search name or username"
                    prefix={<SearchOutlined />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                />
                <Select
                    placeholder="Filter by status"
                    allowClear
                    style={{ width: 160 }}
                    onChange={setStatusFilter}
                >
                    <Option value="pending">Pending</Option>
                    <Option value="approved">Approved</Option>
                    <Option value="rejected">Rejected</Option>
                </Select>
                {selectedRowKeys.length > 0 && (
                    <>
                        <Button
                            type="primary"
                            onClick={() => handleBulkAction('approved')}
                        >
                            Approved ({selectedRowKeys.length})
                        </Button>
                        <Button
                            danger
                            onClick={() => setIsRejectModalVisible(true)}
                        >
                            Rejected ({selectedRowKeys.length})
                        </Button>
                    </>
                )}
            </Space>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                rowKey="user_update_request_id"
                loading={loading}
                pagination={{
                    current: meta.page,
                    pageSize: meta.limit,
                    total: meta.total,
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
            />

            {/* DETAIL MODAL */}
            <Modal
                title="Detail Submission Update"
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={900}
            >
                {selectedRecord && (
                    <>
                        <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: 'bold' }}>
                            <Descriptions.Item label="User Code">
                                {selectedRecord.users?.users_code || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={
                                    selectedRecord?.status === 'approved' ? 'green' :
                                        selectedRecord?.status === 'rejected' ? 'red' : 'orange'
                                }>
                                    {(selectedRecord?.status || '-').toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Deskripsi">
                                {selectedRecord?.desc || '-'}
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider>Perubahan Data</Divider>

                        <Table
                            size="small"
                            bordered
                            pagination={false}
                            dataSource={
                                selectedRecord?.users && parsedRecord
                                    ? [
                                        'name', 'username', 'email', 'phone', 'nik', 'npwp', 'users_code',
                                        'address', 'gender', 'bloody_type', 'religion',
                                        'marital_status', 'birthdate', 'image'
                                    ].map((key) => {
                                        const oldVal = selectedRecord.users?.[key];
                                        const newVal = parsedRecord?.[key];
                                        const isDifferent = oldVal !== newVal;

                                        return {
                                            key,
                                            field: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                                            oldValue: renderValue(oldVal),
                                            newValue: renderValue(newVal),
                                            isDifferent,
                                        };
                                    })
                                    : []
                            }
                            columns={[
                                {
                                    title: 'Field',
                                    dataIndex: 'field',
                                    key: 'field',
                                },
                                {
                                    title: 'Data Sekarang',
                                    dataIndex: 'oldValue',
                                    key: 'oldValue',
                                },
                                {
                                    title: 'Data Baru',
                                    dataIndex: 'newValue',
                                    key: 'newValue',
                                    render: (_, record) =>
                                        record.isDifferent ? (
                                            <span style={{ color: '#00315C', fontWeight: 'bold' }}>
                                                {renderValue(record.newValue)}
                                            </span>
                                        ) : (
                                            renderValue(record.newValue)
                                        ),
                                },
                            ]}
                        />


                        <Divider>Approval Timeline</Divider>

                        <Timeline mode="left">
                            <Timeline.Item color="blue">
                                <Typography.Text strong>{selectedRecord?.users?.name || "-"}</Typography.Text><br />
                                <Typography.Text><Tag color="blue">Process</Tag></Typography.Text><br />
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {dateFormat(selectedRecord?.created_at) || "-"}
                                </Typography.Text>
                            </Timeline.Item>
                            {
                                selectedRecord?.status && selectedRecord?.status !== 'process' && (
                                    <Timeline.Item color="blue">
                                        <Typography.Text strong>{selectedRecord?.approved_by?.name || "-"}</Typography.Text><br />
                                        <Typography.Text>
                                            <Tag color={
                                                selectedRecord.status === "approved" ? "green" :
                                                    selectedRecord.status === "rejected" ? "red" : "yellow"
                                            }>
                                                {selectedRecord.status.charAt(0).toUpperCase() + selectedRecord.status.slice(1)}
                                            </Tag>
                                        </Typography.Text><br />
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {dateFormat(selectedRecord?.approval_at) || "-"}
                                        </Typography.Text>
                                    </Timeline.Item>
                                )
                            }
                        </Timeline>
                    </>
                )}
            </Modal>

            {/* REJECT MODAL */}
            <Modal
                title="Rejection Reason"
                open={isRejectModalVisible}
                onOk={() => {
                    if (!rejectReason.trim()) {
                        message.warning('Reason is required');
                        return;
                    }
                    handleBulkAction('rejected', rejectReason);
                    setIsRejectModalVisible(false);
                    setRejectReason('');
                }}
                onCancel={() => {
                    setIsRejectModalVisible(false);
                    setRejectReason('');
                }}
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter the reason for rejection"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                />
            </Modal>
        </div>
    );
};

export default ProfileRequest;
